import { AsyncPipe } from '@angular/common';
import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { SvgIconComponent } from '@ngneat/svg-icon';
import { BadgeModule } from 'primeng/badge';
import { Subject, takeUntil } from 'rxjs';
import { ChatWindowService } from 'src/app/shared/services/chat-window.service';
import { ChatService } from 'src/app/shared/services/chat.service';
import {
  WebSocketService,
  WS_MESSAGE_TYPE,
} from 'src/app/shared/services/web-socket.service';
import { AppConstant } from 'src/app/shared/utilities/app.constant';
import { LoadingComponent } from '../../../shared/components/loading/nonPercentageLoading/loading/loading.component';
import { AudioNotificationService } from '../../../shared/services/audio-notification.service';
import { ChatLiveSupportComponent } from '../chat-live-support/chat-live-support.component';
import { ChatReportComponent } from '../chat-report/chat-report.component';
import { ConfirmationOverlayComponent } from '../confirmation-overlay/confirmation-overlay.component';

@Component({
  selector: 'app-chat-window',
  templateUrl: './chat-window.component.html',
  styleUrls: ['./chat-window.component.scss'],
  imports: [
    ChatReportComponent,
    ChatLiveSupportComponent,
    BadgeModule,
    ConfirmationOverlayComponent,
    LoadingComponent,
    AsyncPipe,
    SvgIconComponent,
  ],
})
export class ChatWindowComponent implements OnInit, OnChanges, OnDestroy {
  @Input() totalUnreadMsg = 0;
  @Input() isShow = false;
  @Output() onClose = new EventEmitter<boolean>();
  @Output() emitTotalUnread = new EventEmitter<number>();

  @ViewChild('confirmationOverlay')
  confirmationOverlay!: ConfirmationOverlayComponent;

  isSoundNotificationEnabled = true;

  readonly currentStep$ = this.chatWindowService.currentStep$;
  readonly isLoading$ = this.chatWindowService.isLoading$;

  private readonly destroy$ = new Subject<void>();
  private readonly handleReadAllBound: (message: AppConstant.Message) => void;

  constructor(
    private chatWindowService: ChatWindowService,
    private chatService: ChatService,
    private _io: WebSocketService,
    private audioNotificationService: AudioNotificationService
  ) {
    this.handleReadAllBound = this.handleReadAll.bind(this);
  }

  ngOnInit() {
    this.registerWebSocketHandlers();
    this.loadNotificationSettings();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['isShow']?.currentValue && this.totalUnreadMsg) {
      this.chatWindowService.setCurrentStep('LIVE');
    }
  }

  ngOnDestroy() {
    this._io.unregisterMessageHanlder(
      WS_MESSAGE_TYPE.USER__READ_ALL,
      this.handleReadAllBound
    );

    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Load sound notification settings from localStorage
   */
  private loadNotificationSettings() {
    const settings = this.audioNotificationService.getSettings();

    this.isSoundNotificationEnabled = settings.enabled;
  }

  /**
   * Toggle sound notification on/off
   */
  toggleSoundNotification() {
    const currentSettings = this.audioNotificationService.getSettings();
    const newSettings = {
      ...currentSettings,
      enabled: !currentSettings.enabled,
    };
    this.audioNotificationService.saveSettings(newSettings);
    this.isSoundNotificationEnabled = newSettings.enabled;
  }

  /**
   * Register WebSocket message handlers for chat events
   */
  private registerWebSocketHandlers() {
    this._io.registerMessageHanlder(
      WS_MESSAGE_TYPE.USER__READ_ALL,
      this.handleReadAllBound
    );
  }

  /**
   * Handle read all event and reset unread count
   */
  private handleReadAll(message: AppConstant.Message) {
    this.setTotalUnread(0);
  }

  showForm(form: AppConstant.Step) {
    this.chatWindowService.setCurrentStep(form);
  }

  /**
   * Set total unread messages and emit to parent
   * Public because it's called from child component template
   */
  setTotalUnread(total: number) {
    this.totalUnreadMsg = total;
    this.emitTotalUnread.emit(this.totalUnreadMsg);
  }

  showCloseConfirmation() {
    const currentStep = this.chatWindowService.getCurrentStep();
    if (currentStep === 'LIVE') {
      this.confirmationOverlay?.show();
    } else {
      this.closeChatWindow();
    }
  }

  hideChatWindow() {
    this.closeChat();
  }

  closeChatWindow() {
    this.closeChat();
    this.chatService
      .addEvent(AppConstant.SupportCaseEvents.USER__LEFT_CHAT)
      .pipe(takeUntil(this.destroy$))
      .subscribe();
  }

  /**
   * Close chat window and reset state - extracted common logic
   */
  private closeChat() {
    this.onClose.emit(false);
    this.chatWindowService.setCurrentStep();
  }

  goNextStep() {
    this.chatWindowService.setCurrentStep();
  }
}
